import type {
  AccountLifecycleFailure,
  AccountLifecycleResponse,
} from "../Types/accountLifecycle";

type AxiosLikeError = {
  response?: {
    status?: number;
    data?: {
      detail?: string | { msg?: string }[];
      limit_context?: LimitContext;
    };
  };
};

export type LimitContext = {
  used?: number;
  budget?: number;
  additional?: number;
};

function extractDetailMessage(error: AxiosLikeError): string | null {
  const detail = error.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    if (typeof first === "object" && first?.msg) {
      return first.msg;
    }
  }
  return null;
}

export function extractLimitContext(error: unknown): LimitContext | undefined {
  const axiosError = error as AxiosLikeError;
  return axiosError.response?.data?.limit_context;
}

export function formatMonthlyBudgetLimitMessage(
  detail: string | null,
  options?: {
    action?: "create" | "unarchive";
    limitContext?: LimitContext;
  }
): string {
  const ctx = options?.limitContext;
  const action = options?.action ?? "create";

  if (ctx?.budget != null && ctx?.used != null) {
    if (action === "unarchive") {
      return `Can't unarchive — this month's new-account budget is full (${ctx.used} of ${ctx.budget} used). Archive frees active slots without using budget.`;
    }
    return `Can't create account — this month's new-account budget is full (${ctx.used} of ${ctx.budget} used). Archive frees active slots without using budget.`;
  }

  if (detail?.trim()) {
    return detail;
  }

  if (action === "unarchive") {
    return "Can't unarchive — the account limit or this month's new-account budget is full.";
  }

  return "Can't create account — the account limit or this month's new-account budget is full.";
}

function formatLifecycleFailureLabel(failure: AccountLifecycleFailure): string {
  const name = failure.account_name?.trim();
  if (name) {
    return `${name} (${failure.error})`;
  }
  return `${failure.account_id} (${failure.error})`;
}

export function formatLifecycleFailures(
  failures: AccountLifecycleFailure[],
  mode: "archive" | "unarchive"
): string {
  if (failures.length === 0) {
    return "";
  }

  const action = mode === "archive" ? "archived" : "unarchived";
  const countLabel =
    failures.length === 1
      ? "1 account could not be"
      : `${failures.length} accounts could not be`;
  const details = failures.map(formatLifecycleFailureLabel).join("; ");

  return `${countLabel} ${action}: ${details}`;
}

export function getArchiveBatchResultMessage(
  response: AccountLifecycleResponse,
  mode: "archive" | "unarchive"
): string {
  if (response.failures.length > 0) {
    return formatLifecycleFailures(response.failures, mode);
  }

  return mode === "archive"
    ? "Failed to archive account. Please try again."
    : "Failed to unarchive account. Please try again.";
}

export function getUnarchiveErrorMessage(error: unknown): string {
  const axiosError = error as AxiosLikeError;
  const status = axiosError.response?.status;
  const detail = extractDetailMessage(axiosError);

  if (status === 402) {
    return formatMonthlyBudgetLimitMessage(detail, {
      action: "unarchive",
      limitContext: axiosError.response?.data?.limit_context,
    });
  }

  if (status === 403) {
    return detail ?? "You don't have permission to unarchive this account.";
  }

  return detail ?? "Failed to unarchive account. Please try again.";
}

export function getArchiveErrorMessage(error: unknown): string {
  const axiosError = error as AxiosLikeError;
  const status = axiosError.response?.status;
  const detail = extractDetailMessage(axiosError);

  if (status === 403) {
    return detail ?? "You don't have permission to archive this account.";
  }

  return detail ?? "Failed to archive account. Please try again.";
}
